/**
 * IndustryManagerDialog — Oct 2026
 *
 * Workspace-scoped industry master editor. Admins can:
 *   • Add new industries beyond the built-in catalog.
 *   • Add new sub-industries under any industry (builtin or custom).
 *   • Delete custom entries (builtin entries are locked).
 *   • Review "unmatched" industry values that leaked in via bulk import
 *     and one-click add any of them to the catalog.
 *
 * Reads/writes `/api/v1/workspaces/{ws}/industries*` endpoints.
 */
import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { apiErrorMessage } from "@/lib/apiError";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Plus, Trash2, Sparkles, AlertCircle } from "lucide-react";

export default function IndustryManagerDialog({ open, onOpenChange, wsId, catalog, onChange }) {
  const [newIndustry, setNewIndustry] = useState("");
  const [newSubParent, setNewSubParent] = useState("");
  const [newSub, setNewSub] = useState("");
  const [unmatched, setUnmatched] = useState([]);
  const [loadingUnmatched, setLoadingUnmatched] = useState(false);
  const [busyKey, setBusyKey] = useState(null); // generic row-level busy indicator

  const builtinSet = useMemo(() =>
    new Set((catalog?.builtin_industries || []).map((s) => s.toLowerCase())),
    [catalog?.builtin_industries]);
  const customIndustries = catalog?.custom_industries || [];
  const customSubs = catalog?.custom_sub_industries || {};

  // Pull unmatched list every time the dialog opens.
  useEffect(() => {
    if (!open || !wsId) return;
    setLoadingUnmatched(true);
    api.get(`/workspaces/${wsId}/industries/unmatched`)
      .then(({ data }) => setUnmatched(data?.unmatched || []))
      .catch(() => setUnmatched([]))
      .finally(() => setLoadingUnmatched(false));
  }, [open, wsId]);

  const addIndustry = async (name) => {
    const n = (name || "").trim();
    if (!n) { toast.error("Enter an industry name"); return; }
    setBusyKey(`add:${n}`);
    try {
      const { data } = await api.post(`/workspaces/${wsId}/industries`, { name: n });
      onChange?.(data);
      setUnmatched((prev) => prev.filter((u) => u.industry.toLowerCase() !== n.toLowerCase()));
      toast.success(`Added "${n}"`);
      if (name === newIndustry) setNewIndustry("");
    } catch (e) {
      toast.error(apiErrorMessage(e, "Failed to add industry"));
    } finally { setBusyKey(null); }
  };

  const addSub = async () => {
    const industry = (newSubParent || "").trim();
    const sub = (newSub || "").trim();
    if (!industry || !sub) { toast.error("Pick an industry and type a sub-industry"); return; }
    setBusyKey(`addsub:${industry}:${sub}`);
    try {
      const { data } = await api.post(`/workspaces/${wsId}/industries/sub`, {
        industry, sub_industry: sub,
      });
      onChange?.(data);
      toast.success(`Added "${sub}" under ${industry}`);
      setNewSub("");
    } catch (e) {
      toast.error(apiErrorMessage(e, "Failed to add sub-industry"));
    } finally { setBusyKey(null); }
  };

  const deleteIndustry = async (name) => {
    if (!confirm(`Remove "${name}"? Existing leads keep this value but the industry drops off the master list.`)) return;
    setBusyKey(`del:${name}`);
    try {
      const { data } = await api.delete(`/workspaces/${wsId}/industries/${encodeURIComponent(name)}`);
      onChange?.(data);
      toast.success("Industry removed");
    } catch (e) {
      toast.error(apiErrorMessage(e, "Delete failed"));
    } finally { setBusyKey(null); }
  };

  const deleteSub = async (industry, sub) => {
    if (!confirm(`Remove sub-industry "${sub}"?`)) return;
    setBusyKey(`delsub:${industry}:${sub}`);
    try {
      const { data } = await api.delete(
        `/workspaces/${wsId}/industries/${encodeURIComponent(industry)}/sub`,
        { params: { sub } });
      onChange?.(data);
      toast.success("Sub-industry removed");
    } catch (e) {
      toast.error(apiErrorMessage(e, "Delete failed"));
    } finally { setBusyKey(null); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-none max-w-3xl w-[calc(100vw-1rem)] max-h-[92vh] overflow-y-auto p-0" data-testid="industry-manager-dialog">
        <DialogHeader className="p-4 border-b border-border sticky top-0 bg-background z-10">
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-primary" />
            <span>Manage industries</span>
            <span className="text-xs font-normal text-muted-foreground">
              — {(catalog?.builtin_industries?.length || 0)} built-in · {customIndustries.length} custom
            </span>
          </DialogTitle>
        </DialogHeader>

        <div className="p-4 space-y-6">
          {/* ── Unmatched from imports ───────────────────────────────── */}
          <section data-testid="industry-manager-unmatched">
            <div className="flex items-center gap-2 mb-2">
              <AlertCircle className="w-4 h-4 text-amber-600" />
              <h3 className="text-sm font-semibold">Unmatched values from leads</h3>
              {loadingUnmatched && <span className="text-[11px] text-muted-foreground">loading…</span>}
            </div>
            {unmatched.length === 0 && !loadingUnmatched ? (
              <div className="text-xs text-muted-foreground italic border border-dashed border-border p-3">
                All industry values on your leads match the catalog. 👍
              </div>
            ) : (
              <div className="border border-border max-h-[220px] overflow-y-auto divide-y divide-border">
                {unmatched.slice(0, 60).map((u) => (
                  <div key={u.industry} className="flex items-center gap-2 px-3 py-1.5 text-xs"
                       data-testid={`industry-unmatched-row-${u.industry}`}>
                    <span className="flex-1 truncate" title={u.industry}>{u.industry}</span>
                    <span className="text-[10px] text-muted-foreground tabular-nums">
                      {u.count} lead{u.count === 1 ? "" : "s"}
                    </span>
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-6 rounded-none text-[10px] px-2"
                      disabled={busyKey === `add:${u.industry}`}
                      onClick={() => addIndustry(u.industry)}
                      data-testid={`industry-unmatched-add-${u.industry}`}
                    >
                      <Plus className="w-3 h-3 mr-1" /> Add to catalog
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </section>

          {/* ── Add new industry ─────────────────────────────────────── */}
          <section>
            <h3 className="text-sm font-semibold mb-2">Add a new industry</h3>
            <div className="flex gap-2">
              <Input
                value={newIndustry}
                onChange={(e) => setNewIndustry(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") addIndustry(newIndustry); }}
                placeholder="e.g. Aerospace & Defence"
                className="h-8 rounded-none text-sm"
                data-testid="industry-new-input"
              />
              <Button
                size="sm"
                className="rounded-none h-8"
                disabled={!newIndustry.trim() || busyKey?.startsWith("add:")}
                onClick={() => addIndustry(newIndustry)}
                data-testid="industry-new-add-btn"
              >
                <Plus className="w-3 h-3 mr-1" /> Add
              </Button>
            </div>
          </section>

          {/* ── Add sub-industry ─────────────────────────────────────── */}
          <section>
            <h3 className="text-sm font-semibold mb-2">Add a sub-industry</h3>
            <div className="grid grid-cols-1 sm:grid-cols-[1fr_1fr_auto] gap-2">
              <Select value={newSubParent || "__pick__"} onValueChange={(v) => setNewSubParent(v === "__pick__" ? "" : v)}>
                <SelectTrigger className="h-8 rounded-none text-xs" data-testid="industry-sub-parent-select">
                  <SelectValue placeholder="Pick parent industry" />
                </SelectTrigger>
                <SelectContent className="max-h-[280px]">
                  <SelectItem value="__pick__">— Pick parent industry —</SelectItem>
                  {(catalog?.industries || []).map((i) => <SelectItem key={i} value={i}>{i}</SelectItem>)}
                </SelectContent>
              </Select>
              <Input
                value={newSub}
                onChange={(e) => setNewSub(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") addSub(); }}
                placeholder="e.g. Drones / UAV"
                className="h-8 rounded-none text-sm"
                disabled={!newSubParent}
                data-testid="industry-new-sub-input"
              />
              <Button
                size="sm"
                className="rounded-none h-8"
                disabled={!newSubParent || !newSub.trim()}
                onClick={addSub}
                data-testid="industry-new-sub-add-btn"
              >
                <Plus className="w-3 h-3 mr-1" /> Add
              </Button>
            </div>
          </section>

          {/* ── Custom entries ───────────────────────────────────────── */}
          <section>
            <h3 className="text-sm font-semibold mb-2">Workspace-only entries</h3>
            {customIndustries.length === 0 && Object.keys(customSubs).length === 0 ? (
              <div className="text-xs text-muted-foreground italic border border-dashed border-border p-3">
                No custom entries yet. Everything the dropdowns show is from the built-in catalog.
              </div>
            ) : (
              <div className="space-y-2">
                {customIndustries.map((name) => (
                  <div key={name} className="flex items-center gap-2 border border-border px-3 py-1.5"
                       data-testid={`industry-custom-row-${name}`}>
                    <span className="text-xs font-semibold flex-1 truncate">{name}</span>
                    <span className="text-[9px] uppercase tracking-widest text-emerald-700 bg-emerald-50 border border-emerald-200 px-1.5">custom</span>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-6 w-6 rounded-none p-0 text-rose-600 hover:text-rose-700"
                      disabled={busyKey === `del:${name}`}
                      onClick={() => deleteIndustry(name)}
                      title="Remove this custom industry"
                      data-testid={`industry-custom-del-${name}`}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                ))}
                {Object.entries(customSubs).map(([parent, subs]) =>
                  (subs || []).map((sub) => (
                    <div key={`${parent}::${sub}`} className="flex items-center gap-2 border border-border px-3 py-1.5"
                         data-testid={`industry-custom-sub-row-${parent}-${sub}`}>
                      <span className="text-xs text-muted-foreground">{parent}</span>
                      <span className="text-xs">›</span>
                      <span className="text-xs font-semibold flex-1 truncate">{sub}</span>
                      <span className="text-[9px] uppercase tracking-widest text-emerald-700 bg-emerald-50 border border-emerald-200 px-1.5">custom</span>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-6 w-6 rounded-none p-0 text-rose-600 hover:text-rose-700"
                        disabled={busyKey === `delsub:${parent}:${sub}`}
                        onClick={() => deleteSub(parent, sub)}
                        title="Remove this custom sub-industry"
                        data-testid={`industry-custom-sub-del-${parent}-${sub}`}
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  ))
                )}
              </div>
            )}
          </section>
        </div>
      </DialogContent>
    </Dialog>
  );
}
